'use client'

import { createContext, useContext, useMemo, useRef } from 'react'

import { logEvent } from '@/shared/external/firebase'

import {
  DEFAULT_RANK_TYPE,
  HOME_EVENT,
  HOME_TRACK_CODE,
  HOME_TRACK_LABEL,
  HOME_TRACK_TYPE,
  RANK_TYPE_LABEL,
} from '../_constants'

import type { TRankingCriteria } from '../_types'
import type { ReactNode } from 'react'

type TTrackParams = Record<string, string | number | boolean | null>

export interface IHomeTracker {
  setRankType: (criteria: TRankingCriteria) => void
  track: (eventName: string, params?: TTrackParams) => void
  pageShow: () => void
  logoClick: () => void
  sectionShow: (code: string, label: string) => void
  itemShow: (
    code: string,
    type: string,
    label: string,
    extra?: TTrackParams
  ) => void
  itemClick: (
    code: string,
    type: string,
    label: string,
    extra?: TTrackParams
  ) => void
}

const NOOP_TRACKER: IHomeTracker = {
  setRankType: () => {},
  track: () => {},
  pageShow: () => {},
  logoClick: () => {},
  sectionShow: () => {},
  itemShow: () => {},
  itemClick: () => {},
}

const HomeTrackingContext = createContext<IHomeTracker | null>(null)

export const HomeTrackingProvider = ({ children }: { children: ReactNode }) => {
  const rankTypeRef = useRef<string>(DEFAULT_RANK_TYPE)

  const tracker = useMemo<IHomeTracker>(() => {
    const track = (eventName: string, params: TTrackParams = {}) => {
      logEvent(eventName, { rank_type: rankTypeRef.current, ...params })
    }

    return {
      setRankType: criteria => {
        rankTypeRef.current = RANK_TYPE_LABEL[criteria]
      },
      track,
      pageShow: () =>
        track(HOME_EVENT.PAGE_SHOW, {
          code: HOME_TRACK_CODE.PAGE,
          type: HOME_TRACK_TYPE.PAGE,
          label: HOME_TRACK_LABEL.PAGE,
        }),
      logoClick: () =>
        track(HOME_EVENT.LOGO_CLICK, {
          code: HOME_TRACK_CODE.HEADER,
          type: HOME_TRACK_TYPE.LOGO,
          label: HOME_TRACK_LABEL.LOGO,
        }),
      sectionShow: (code, label) =>
        track(HOME_EVENT.SECTION_SHOW, {
          code,
          type: HOME_TRACK_TYPE.SECTION,
          label,
        }),
      itemShow: (code, type, label, extra = {}) =>
        track(HOME_EVENT.ITEM_SHOW, { code, type, label, ...extra }),
      itemClick: (code, type, label, extra = {}) =>
        track(HOME_EVENT.ITEM_CLICK, { code, type, label, ...extra }),
    }
  }, [])

  return (
    <HomeTrackingContext.Provider value={tracker}>
      {children}
    </HomeTrackingContext.Provider>
  )
}

export const useHomeTracking = (): IHomeTracker =>
  useContext(HomeTrackingContext) ?? NOOP_TRACKER
